/* Membership edits refresh Shopify-rendered sections without leaving the cart. */
(function () {
  if (window.MilesCommerceCart) return;
  const model = window.MilesCartModel;
  const mutations = new Set();
  let quantityPending = false;
  let invalidated = false;
  let restoreFocus;
  const busy = () => mutations.size > 0 || quantityPending;
  function sync() {
    const picker = document.querySelector('miles-cart-membership');
    const checkout = document.getElementById('checkout');
    if (checkout) checkout.disabled = busy() || invalidated || checkout.dataset.milesValid !== 'true' || !!picker?.dirty;
    picker?.querySelectorAll('input, button').forEach((control) => {
      const input = control.hasAttribute('data-term-step') ? document.getElementById(control.getAttribute('aria-controls')) : null;
      const atLimit = input && input.value !== '' && (Number(control.dataset.termStep) < 0
        ? Number(input.value) <= Number(input.min) : Number(input.value) >= Number(input.max));
      control.disabled = busy() || invalidated || control.dataset.unavailable === 'true' || !!atLimit;
    });
    picker?.querySelectorAll('[data-term-count]').forEach((input) => {
      input.closest('.miles-cart-membership__option').classList.toggle('is-selected', Number(input.value) > 0);
    });
    document.querySelector('cart-items')?.toggleAttribute('inert', busy() || invalidated);
    if (invalidated) document.querySelectorAll('[data-cart-recover]').forEach((link) => { link.hidden = false; });
    if (!busy() && !invalidated && restoreFocus) {
      const focus = restoreFocus;
      restoreFocus = undefined;
      focus();
    }
  }
  window.MilesCommerceCart = {
    lock({ quantityChange = false } = {}) {
      const token = Symbol();
      mutations.add(token);
      // Only the request consuming the debounced edit may clear its pending flag.
      if (quantityChange) quantityPending = false;
      sync();
      return () => { mutations.delete(token); sync(); };
    },
    cancelQuantityChange() { quantityPending = false; sync(); },
    whenIdle(focus) { restoreFocus = focus; sync(); },
    async verifyFresh() {
      if (invalidated) return false;
      const form = document.getElementById('cart');
      const inputs = Array.from(form?.querySelectorAll('input[data-quantity-line-key]') || []);
      if (!inputs.length) return true;
      // Use the server-rendered values, before the customer's pending edit.
      const rendered = { items: inputs.map((input) => ({ key: input.dataset.quantityLineKey, quantity: Number(input.getAttribute('value')) })) };
      const cartUrl = new URL(form.action, window.location.origin);
      cartUrl.pathname += '.js';
      cartUrl.search = '';
      const response = await fetch(cartUrl.href, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('request');
      const current = await response.json();
      if (model.fingerprint(current) === model.fingerprint(rendered)) return true;
      this.invalidate();
      window.location.reload();
      return false;
    },
    invalidate() {
      invalidated = true;
      const checkout = document.getElementById('checkout');
      if (checkout) checkout.dataset.milesValid = 'false';
      sync();
    },
  };
  // Close the interval between a quantity edit and Dawn's debounced request.
  document.addEventListener('change', (event) => {
    if (event.target.matches('#cart input[data-index]')) { quantityPending = true; sync(); }
  }, true);
  let verifiedSubmit = false;
  document.addEventListener('submit', async (event) => {
    if (event.target.id !== 'cart' || verifiedSubmit) return;
    event.preventDefault();
    const checkout = document.getElementById('checkout');
    if (busy() || invalidated || checkout?.disabled) return;
    const form = event.target;
    const submitter = event.submitter || checkout;
    const release = window.MilesCommerceCart.lock();
    try {
      // The native form also writes quantities. A stale tab must refresh before
      // checkout instead of overwriting a newer cart with its older values.
      if (!(await window.MilesCommerceCart.verifyFresh())) return;
      release();
      verifiedSubmit = true;
      form.requestSubmit(submitter);
    } catch (error) {
      window.MilesCommerceCart.invalidate();
      const errors = document.getElementById('cart-errors');
      if (errors) errors.textContent = window.cartStrings.error;
    } finally {
      verifiedSubmit = false;
      release();
    }
  }, true);
  window.addEventListener('pageshow', (event) => {
    // The normal initial pageshow can happen after a customer has already edited
    // quantity. Only a back/forward-cache restore needs to refresh the cart.
    if (event.persisted) window.location.reload();
  });

  class MilesCartMembership extends HTMLElement {
    connectedCallback() {
      this.dirty = false;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.form = this.querySelector('form');
      this.error = this.querySelector('[role="alert"]');
      this.status = this.querySelector('[role="status"]');
      this.addEventListener('input', () => { this.dirty = true; this.status.textContent = ''; sync(); });
      this.addEventListener('change', (event) => {
        if (!event.target.matches('input')) return;
        this.dirty = true;
        this.error.textContent = '';
        this.status.textContent = '';
        // Persist every edit; the rendered footer gates checkout on coverage.
        this.save();
      });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-term-step]');
        if (!button || button.disabled || busy() || invalidated) return;
        event.preventDefault();
        const input = document.getElementById(button.getAttribute('aria-controls'));
        const previous = input.value;
        button.focus({ preventScroll: true });
        if (Number(button.dataset.termStep) > 0) input.stepUp();
        else input.stepDown();
        if (input.value !== previous) input.dispatchEvent(new Event('change', { bubbles: true }));
        // A boundary button can become disabled after a rejected edit.
        if (!busy() && button.isConnected && button.disabled) input.focus({ preventScroll: true });
      });
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.save(); });
      sync();
    }
    selectedCounts() {
      if (this.config.plugs === 1) {
        const selected = this.querySelector('input[type="radio"]:checked');
        return model.TERMS.map((_, index) => Number(selected?.value === String(index)));
      }
      return Array.from(this.querySelectorAll('[data-term-count]'), (input) => input.value.trim() === '' ? NaN : Number(input.value));
    }
    async save() {
      if (busy() || invalidated) return;
      const counts = this.selectedCounts();
      if (!model.validTermCounts(counts) || counts.some((count) => count > this.config.plugs)) {
        this.error.textContent = this.config.quantityError;
        sync();
        return;
      }
      const focused = this.querySelector('input:focus, button:focus');
      const focusId = focused?.id || this.querySelector('input:checked')?.id;
      const focusInputId = focused?.getAttribute('aria-controls');
      const sections = [
        { id: 'main-cart-items', selector: '.js-contents' },
        { id: 'main-cart-footer', selector: '.js-contents' },
        { id: 'cart-icon-bubble', selector: '.shopify-section', section: 'cart-icon-bubble' },
        { id: 'cart-live-region-text', selector: '.shopify-section', section: 'cart-live-region-text' },
      ].filter(({ id }) => document.getElementById(id)).map((entry) => ({
        ...entry, section: entry.section || document.getElementById(entry.id).dataset.id,
      }));
      const release = window.MilesCommerceCart.lock();
      this.error.textContent = '';
      this.status.textContent = this.config.updatingMessage;
      this.setAttribute('aria-busy', 'true');
      let mutationStarted = false;
      try {
        const response = await fetch(this.config.cartUrl + '.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('request');
        const cart = await response.json();
        if (model.fingerprint(cart) !== model.fingerprint(this.config.cart)) {
          window.MilesCommerceCart.invalidate();
          window.location.reload();
          return;
        }
        const updates = model.updatesFor(cart, this.config.catalog, counts);
        mutationStarted = true;
        const result = await fetch(this.config.updateUrl + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: sections.map(({ section }) => section), sections_url: this.config.cartUrl }),
        });
        const updated = await result.json();
        if (!result.ok || updated.errors || updated.status) throw new Error('request');
        // Validate every fragment before replacing any: Shopify can return null
        // sections even when the cart mutation itself succeeds.
        const fragments = sections.map(({ id, section, selector }) => {
          const markup = updated.sections?.[section];
          if (typeof markup !== 'string') throw new Error('sections');
          const content = new DOMParser().parseFromString(markup, 'text/html').querySelector(selector);
          if (!content) throw new Error('sections');
          const container = document.getElementById(id);
          return { target: container.querySelector(selector) || container, content: content.innerHTML };
        });
        fragments.forEach(({ target, content }) => { target.innerHTML = content; });
        // Prices, discount allocation and checkout eligibility come from Liquid.
        release();
        window.MilesCommerceCart.whenIdle(() => {
          const control = focusId && document.getElementById(focusId);
          const focusTarget = control?.disabled ? document.getElementById(focusInputId) : control;
          focusTarget?.focus({ preventScroll: true });
          const picker = document.querySelector('miles-cart-membership');
          if (picker) picker.status.textContent = picker.config.updatedMessage;
        });
      } catch (error) {
        this.error.textContent = error.message === 'unavailable' ? this.config.unavailableError : this.config.requestError;
        this.dirty = true; // A failed/ambiguous request never enables payment against stale UI.
        if (mutationStarted) window.MilesCommerceCart.invalidate();
        this.status.textContent = '';
        this.querySelector('[data-cart-recover]').hidden = false;
        this.removeAttribute('aria-busy');
        release();
      }
    }
  }
  if (!customElements.get('miles-cart-membership')) customElements.define('miles-cart-membership', MilesCartMembership);
})();
